import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
} from "react";
import {
  Backdrop,
  Button,
  Chart,
  ChartNew,
  Chip,
  DevNote,
  FormFieldSet,
  InfoCardNewAdv,
  InfoTimeCardAdv,
  InfoWrapper,
  LogoLoader,
  Modal,
  showToast,
  SmartTooltip,
  useFormHandler,
} from "../../../components";
import { formatCurrency } from "../../../utils/stringUtils";
import { getDashboardData } from "../../../utils/dashboard";
import dayjs from "dayjs";
import { ENDPOINTS, WORKFORCE_API_URL } from "../../../redux/apiConfig";
import axiosRequest from "../../../redux/slices/universal/axiosService";
import { dateFormator, getEmployeeId, mapTableRows } from "../../../utils";
import { usePagination } from "../../../hooks";
import axios from "axios";
import { InvoiceKPICard } from "./Modals/KpiViewModal";
import {
  agingColumns,
  agingRows,
  chargeBackColumns,
  chargeBackRows,
  columns,
  mappedRows,
  purchaseSummaryColumns,
  purchaseSummaryRows,
  reservedColumns,
  reservedRows,
  rows,
} from "../../../assets/data/fundingDashboardData";
import UploadModal from "./Modals/UploadModal";
import ActivityModal from "./Modals/ActivityModal";
import { DATE_RANGE_OPTIONS } from "../../../assets/data/enumData";
import { getDatesByOption } from "../../../utils/wf/date";
import { UploadModalTable, AgingFilterBar } from "./Modals/UploadModalTable";
import {
  ReservedModalTable,
  ReservedFilterBar,
} from "./Modals/ReservedModalTable";
import {
  ChargeBackFilterBar,
  ChargeBackReportTable,
} from "./Modals/ChargeBackReportTable";
import {
  PurchaseSummaryFilterBar,
  PurchaseSummaryReportTable,
} from "./Modals/PurchaseSummaryReportTable";
import ChargeBackUploadModal from "./Modals/ChargeBackUploadModal";
import ReservedUploadModal from "./Modals/ReservedUploadModal";
import PurchaseSummaryUploadModal from "./Modals/PurchaseSummaryUploadModal";
import { useLoginUser } from "../../../hooks/useLoginUser";
import { title } from "framer-motion/client";

const DropdownOptions = [
  // ...(import.meta.env=== "local" ? [] : { label: "Ready to Fund", value: 1 }),
  { label: "Funded", value: 2 },
  { label: "Rejected", value: 3 },
];

// const reservedData = Array.from({ length: 0 }, (_, i) => ({
//   "Client Name": `IBM`,
//   "1-30": `1040${i + 1}`,
//   "30-60": `0`,
//   "60-90": `10400`,
// }));
const reservedData = reservedRows?.map((row) => ({
  Description: (
    <SmartTooltip
      description={row.description}
      size="xs"
      color="black"
      noicon
      tooltipPosition="bottom"
      portal
    >
      <span className="line-clamp-1 min-w-[150px] max-w-[300px]">
        {row.description || ""}
      </span>
    </SmartTooltip>
  ),
  "Applied To A/R": row.total.appliedAR,
  "Applied To Advance": row.total.appliedAdvance,
  "Applied To Fee": row.total.appliedFee,
  "Reserve Amount": row.total.reserveAmount,
}));

// const agingData = Array.from({ length: 5 }, (_, i) => ({
//   "Client Name": `IBM`,
//   "1-30": `1040${i + 1}`,
//   "30-60": `0`,
//   "60-90": `10400`,
// }));
const agingData = agingRows.map((client) => ({
  "Client Name": (
    <SmartTooltip
      description={client.client}
      size="xs"
      color="black"
      noicon
      tooltipPosition="bottom"
      portal
    >
      <span className="line-clamp-1 min-w-[150px] max-w-[300px]">
        {client.client || ""}
      </span>
    </SmartTooltip>
  ),
  Balance: client.total.bal,
  "1-30 Days": client.total.d1_30,
  "31-60 Days": client.total.d31_60,
  "61-90 Days": client.total.d61_90,
  "91-120 Days": client.total.d91_120,
  "121+ Days": client.total.d121,
}));

// const chargeBackData = Array.from({ length: 5 }, (_, i) => ({
//   "Client Name": `IBM`,
//   "Invoice Number": `1040${i + 1}`,
//   "Time Period": `0`,
//   "Charge Back Amount": 8000,
// }));
const chargeBackData = chargeBackRows.map((client) => ({
  Customer: (
    <SmartTooltip
      description={client.customer}
      size="xs"
      color="black"
      noicon
      tooltipPosition="bottom"
      portal
    >
      <span className="line-clamp-1 min-w-[150px] max-w-[300px]">
        {client.customer || ""}
      </span>
    </SmartTooltip>
  ),
  "Amount Paid": client?.total?.amountPaid,
  "Give Back": client?.total?.giveBack,
  "Charge Back": client?.total?.chargeBack,
}));

// const RejectedData = Array.from({ length: 5 }, (_, i) => ({
//   "Client Name": `IBM`,
//   "Invoice Number": `1040${i + 1}`,
//   "Time Period": `0`,
//   "Rejected Invoice Amount": 8000,
// }));
const RejectedData = purchaseSummaryRows?.rows?.map((client) => ({
  Customer: (
    <SmartTooltip
      description={client?.customer}
      size="xs"
      color="black"
      noicon
      tooltipPosition="bottom"
      portal
    >
      <span className="line-clamp-1 min-w-[150px] max-w-[300px]">
        {client.customer || ""}
      </span>
    </SmartTooltip>
  ),
  "Debit Amount": client?.debitAmount,
  "Credit Amount": client?.creditAmount,
  "Total Amount": client?.totalAmount,
}));

export const fetchFundingCards = async (from, to) => {
  if (!from || !to) {
    return [
      {
        title: "Total Funded",
        value: formatCurrency(0),
      },
      {
        title: "Funded",
        value: formatCurrency(0),
      },
      {
        title: "Ready To Fund",
        value: formatCurrency(0),
      },
      {
        title: "Rejected for Funding",
        value: formatCurrency(0),
      },
    ];
  }
  try {
    const dashboard = await getDashboardData(
      {
        fundingInvoices: {
          type: "AG_STATS",
          value: [
            true,
            dayjs(from).format("YYYY-MM-DD"),
            dayjs(to).format("YYYY-MM-DD"),
          ],
        },
      },
      "Invoice",
    );
    let cardsResponse = dashboard?.[0] || {};
    return [
      {
        title: "Total Funded",
        value: formatCurrency(cardsResponse?.total),
      },
      {
        title: "Funded",
        value: formatCurrency(cardsResponse?.funded),
      },
      {
        title: "Ready To Fund",
        value: formatCurrency(cardsResponse?.readyToFund),
      },
      {
        title: "Rejected for Funding",
        value: formatCurrency(cardsResponse?.rejected),
      },
    ];
  } catch (error) {
    console.error("Error in fetching funding dashboard cards", error);
  }
};
//fetches the latest funding aging report
export const fetchAgingReport = async () => {
  try {
    const response = await axiosRequest({
      method: "GET",
      url: ENDPOINTS.WF_REPORTS.GET,
      params: {
        coll: "Report",
        fil: JSON.stringify({
          title: { type: "NONE", value: "Aging Report" },
        }),
        sort: JSON.stringify({ addedOn: -1 }),
      },
    });
    return response?.data;
  } catch (error) {
    console.error(
      "Error in fetching fetchAgingReport for latest funding aging report",
      error,
    );
  }
};

//fetches the latest funding chargeback report
export const fetchChargeBackReport = async () => {
  try {
    const response = await axiosRequest({
      method: "GET",
      url: ENDPOINTS.WF_REPORTS.GET,
      params: {
        coll: "Report",
        fil: JSON.stringify({
          title: { type: "NONE", value: "Chargeback Report" },
        }),
        sort: JSON.stringify({ addedOn: -1 }),
      },
    });
    return response?.data;
  } catch (error) {
    console.error(
      "Error in fetching fetchChargeBackReport for latest funding chargeback report",
      error,
    );
  }
};


//fetches the latest funding Purchase Summary
export const fetchPurchaseSummaryReport = async () => {
  try {
    const response = await axiosRequest({
      method: "GET",
      url: ENDPOINTS.WF_REPORTS.GET,
      params: {
        coll: "Report",
        fil: JSON.stringify({
          title: { type: "NONE", value: "Purchase Summary" },
        }),
        sort: JSON.stringify({ addedOn: -1 }),
      },
    });
    return response?.data;
  } catch (error) {
    console.error(
      "Error in fetching fetchPurchaseSummaryReport for latest funding PurchaseSummaryReport",
      error,
    );
  }
};

//fetches the latest funding Reserve Detail Report
export const fetchReserveDetailReport = async () => {
  try {
    const response = await axiosRequest({
      method: "GET",
      url: ENDPOINTS.WF_REPORTS.GET,
      params: {
        coll: "Report",
        fil: JSON.stringify({
          title: { type: "NONE", value: "Reserve Detail Report" },
        }),
        sort: JSON.stringify({ addedOn: -1 }),
      },
    });
    return response?.data;
  } catch (error) {
    console.error(
      "Error in fetching fetchReserveDetailReport for latest funding fetchReserveDetailReport",
      error,
    );
  }
};

const FilterBar = ({
  setActivityOpen,
  uploadOpen,
  setUploadOpen,
  formData,
  handleChange,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const isCustomRange = formData.reportPeriod === "custom";

  const handleDateRangeSelect = (val) => {
    handleChange("reportPeriod", val);

    if (val === "custom") {
      handleChange("fromDate", null);
      handleChange("toDate", null);
    } else {
      const range = getDatesByOption(val);
      if (range) {
        handleChange("fromDate", range.from);
        handleChange("toDate", range.to);
      }
    }
    setShowDropdown(false);
  };

  return (
    <div className="bg-white py-3 flex items-end gap-3 flex-wrap">
      {/* Date Range Select */}
      <FormFieldSet
        title="Report Period"
        placeholder="select Date Range"
        type="select"
        options={DATE_RANGE_OPTIONS}
        value={formData.reportPeriod}
        onChange={handleDateRangeSelect}
        showDropdown={showDropdown}
        // onClick={() => setShowDropdown(true)}
      />

      {/* From - Only for Custom */}
      {isCustomRange && (
        <FormFieldSet
          title="From"
          type="datepicker"
          placeholder="select Date"
          value={formData.fromDate}
          onChange={(val) => handleChange("fromDate", val)}
          showDropdown={false}
        />
      )}

      {/* To - Only for Custom */}
      {isCustomRange && (
        <FormFieldSet
          title="To"
          type="datepicker"
          placeholder="select Date"
          value={formData.toDate}
          onChange={(val) => handleChange("toDate", val)}
          showDropdown={false}
          minDate={formData.fromDate ? new Date(formData.fromDate) : undefined}
        />
      )}

      <FormFieldSet
        noTitle
        type="text"
        placeholder="search"
        value={formData.search}
        onChange={(val) => handleChange("search", val)}
      />

      <div className="flex-1" />

      <Button
        title={"Activity"}
        variant={"outlined"}
        onClick={() => setActivityOpen(true)}
      />
      <Button
        icon={"upload"}
        title={"Upload"}
        onClick={() => setUploadOpen(true)}
      />
    </div>
  );
};

const FundingDashboard = () => {
  const initialFormData = {
    search: "",
    reportPeriod: "",
    fromDate: "",
    toDate: "",
    modalFromDate: "",
    modalToDate: "",
    modalDate: "",
    modalPreviewFromDate: "",
    modalPreviewToDate: "",
    modalPreviewDate: "",
    modalReportName: "",
    modalFile: null,
  };
  const { formData, errors, handleChange, setFormData } = useFormHandler(
    initialFormData,
    {},
  );
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const [activityOpen, setActivityOpen] = useState(false);
  const [chargeBackUpload, setChargeBackUpload] = useState(false);
  const [agingUpload, setAgingUpload] = useState(false);
  const [reservedUpload, setReservedUpload] = useState(false);
  const [purchaseSummaryUpload, setPurchaseSummaryUpload] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const [kpiModalType, setKpiModalType] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [totalCounts, setTotalCounts] = useState({
    inv: 0,
  });
  const [dates, setDates] = useState({
    cards: {
      from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
    },
    invoices: {
      from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({
    overviewCards: [
      {
        title: "Total Funded",
        value: formatCurrency(0),
      },
      {
        title: "Funded",
        value: formatCurrency(0),
      },
      {
        title: "Ready To Fund",
        value: formatCurrency(0),
      },
      {
        title: "Rejected for Funding",
        value: formatCurrency(0),
      },
    ],
    invoicesFunding: [],
  });

  /**
   * Handles changes to the date range cards
   * and fetches the respective data accordingly
   * @param {string} key - the key of the date range card
   * @param {Object} value - the value of the date range card
   * @example handleDateRangeCards("invoices", { from: new Date(), to: new Date() })
   */
  // const handleDateRangeCards = async (key, value) => {
  //   setDates((prevState) => ({ ...prevState, [key]: value }));
  //   if (key === "invoices" || key === "cards") {
  //     const invoices = await fetchFundingInvoices(
  //       value.from,
  //       value.to,
  //       invLimit,
  //       invPage,
  //     );
  //     const dashboardCards = await fetchFundingCards(value.from, value.to);
  //     setData((prev) => ({
  //       ...prev,
  //       overviewCards: dashboardCards,
  //       invoicesFunding: invoices?.invoicesFunding || [],
  //     }));
  //     setTotalCounts((prev) => ({
  //       ...prev,
  //       inv: invoices?.invoicesFundingTotal,
  //     }));
  //   }
  // };
  const handleDateRangeCards = async (key, value) => {
    if (!value) return;

    const from = new Date(value.getFullYear(), value.getMonth(), 1);
    const to = new Date(value.getFullYear(), value.getMonth() + 1, 0);

    setDates((prevState) => ({
      ...prevState,
      [key]: { from, to },
    }));

    if (key === "invoices" || key === "cards") {
      const invoices = await fetchFundingInvoices(from, to, invLimit, invPage);

      const dashboardCards = await fetchFundingCards(from, to);

      setData((prev) => ({
        ...prev,
        overviewCards: dashboardCards,
        invoicesFunding: invoices?.invoicesFunding || [],
      }));

      setTotalCounts((prev) => ({
        ...prev,
        inv: invoices?.invoicesFundingTotal,
      }));
    }
  };
  const {
    page: invPage,
    limit: invLimit,
    setPage: invSetPage,
    handleLimitChange: invHandleLimitChange,
  } = usePagination(5);
  const {
    page: reservedPage,
    limit: reservedLimit,
    setPage: reservedSetPage,
    handleLimitChange: reservedHandleLimitChange,
  } = usePagination(5);

  /**
   * Sets the selected invoice ids in the component's state
   * ids - an array of invoice ids
   */
  const handleSelection = (ids) => {
    setSelectedIds(ids);
  };
  const handleFundingSelect = async (value) => {
    setSelectedOption(value);
  };
  const handlePurchaseSummaryUpload = () => {
    setPurchaseSummaryUpload(true);
  };
  const handleChargeBackUpload = () => {
    setChargeBackUpload(true);
  };
  const handleAgingUpload = () => {
    setAgingUpload(true);
  };
  const handleReservedUpload = () => {
    setReservedUpload(true);
  };

  const handleExport = async () => {
    try {
      if (!selectedIds.length) {
        showToast("Please select at least one invoice", "error");
        return;
      }
      showToast("Downloading file...");
      setIsLoading(true);
      const response = await axios.post(
        `${WORKFORCE_API_URL}/invoices/export-funding-info`,
        {
          coll: "Invoice",
          payload: {
            dates: {
              from: dayjs(dates?.cards?.from).format("YYYY-MM-DD"),
              to: dayjs(dates?.cards?.to).format("YYYY-MM-DD"),
            },
            ids: selectedIds,
          },
        },
        { responseType: "blob", withCredentials: true },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      // const url = window.URL.createObjectURL(new Blob([]));
      const link = document.createElement("a");
      link.href = url;
      link.download =
        response.headers["content-disposition"]?.split("filename=")[1] ||
        "funding-info.zip";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showToast("Error downloading file", "error");
      console.error("Error downloading file:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const invoiceStatusChange = async () => {
    try {
      if (!selectedOption) {
        showToast("Please select an option", "error");
        return;
      }
      setIsLoading(true);
      const response = await axiosRequest({
        method: "PATCH",
        url: ENDPOINTS.INVOICES.BULKUPDATE,
        params: {
          coll: "Invoice",
        },
        data: {
          payload: [
            {
              type: "UPDATEMANY",
              value: {
                type: "SET",
                value: {
                  funding: {
                    status: selectedOption,
                    on: new Date(),
                  },
                },
                fil: {
                  uiid: {
                    type: "IN",
                    value: selectedIds,
                  },
                },
              },
            },
          ],
        },
      });
      if (!response?.success) {
        showToast("Error updating invoice status", "error");
        return;
      }
      showToast("Invoice Funding Status Updated", "success");
      handleDashboardCalls();
    } catch (error) {
      console.error("invoiceStatusChange Error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFundingInvoices = async (from, to, limit, page) => {
    if (!from || !to) {
      return {
        invoicesFunding: [],
        invoicesFundingTotal: 0,
      };
    }
    try {
      const payload = {
        coll: "Invoice",
        lim: limit,
        pg: page,
        useAggregate: true,
        fil: JSON.stringify({
          fundingInvoices: {
            type: "AG_LOOKUP_DATA",
            value: [
              true,
              dayjs(from).format("YYYY-MM-DD"),
              dayjs(to).format("YYYY-MM-DD"),
            ],
          },
        }),
      };
      const [invoices] = await Promise.allSettled([
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.INVOICES.GET_ALL,
          params: payload,
        }),
      ]);
      const invoicesFunding = invoices?.value?.data?.data[0]?.data || [];
      const invoicesFundingTotal =
        invoices?.value?.data?.data[0]?.count?.[0]?.total || 0;
      // setTotalCounts((prev) => ({ ...prev, inv: invoicesFundingTotal }));
      const fundindInvoicesStatus = {
        1: {
          label: "Ready to Fund",
          color: "primary",
          className: "text-primary rounded px-3 py-1",
        },
        2: {
          label: "Funded",
          color: "success",
          className: "text-success rounded px-3 py-1",
        },
        3: {
          label: "Rejected",
          color: "danger",
          className: "text-danger rounded px-3 py-1",
        },
      };
      const formatInvoiceActivityPeriod = (raw) => {
        if (!raw) return "";

        const dates = raw.match(/\d{2}\/\d{2}\/\d{4}/g);
        if (!dates || dates.length < 2) return "";

        return dates
          .slice(0, 2)
          .map((d) => dayjs(d, "MM/DD/YYYY").format("MM/DD/YYYY"))
          .join(" - ");
      };

      // const mappedInvoices = invoicesFunding.map((i) => ({
      //   id: i.uiid || "",
      //   "Emp Name": i?.employeeName || "",
      //   // "Invoice Number": i.invoiceNum,
      //   "Emp Code": getEmployeeId(i?.employeeId, i?.employmentType) || "",
      //   Client: i?.clientName || "",
      //   "Time Period": formatInvoiceActivityPeriod(i?.timePeriod),
      //   "Invoice Number": i?.invoiceNum || "",
      //   "Invoice Date": dayjs(i.invoiceDate).format("MM/DD/YYYY"),
      //   "Funding Status": (
      //     <Chip
      //       label={fundindInvoicesStatus[i?.fundingStatus]?.label}
      //       color={fundindInvoicesStatus[i?.fundingStatus]?.color}
      //       className={fundindInvoicesStatus[i?.fundingStatus]?.className}
      //     />
      //   ),
      //   // "Invoice Amount": formatCurrency(i?.amounts?.invoiced || 0),
      //   "Invoice Amount": i?.amounts?.invoiced || 0,
      // }));
      const mappedInvoices = invoicesFunding.map((i) => ({
        id: i.uiid || "",
        "Emp Name": (
          <SmartTooltip
            description={i?.employeeName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal
          >
            <span className="line-clamp-1">{i?.employeeName || ""}</span>
          </SmartTooltip>
        ),
        "Emp Code": (
          <SmartTooltip
            description={getEmployeeId(i?.employeeId, i?.employmentType)}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal
          >
            <span className="line-clamp-1">
              {getEmployeeId(i?.employeeId, i?.employmentType) || ""}
            </span>
          </SmartTooltip>
        ),
        Client: (
          <SmartTooltip
            description={i?.clientName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal
          >
            <span className="line-clamp-1">{i?.clientName || ""}</span>
          </SmartTooltip>
        ),
        "Time Period": (
          <SmartTooltip
            description={formatInvoiceActivityPeriod(i?.timePeriod)}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal
          >
            <span className="line-clamp-1">
              {formatInvoiceActivityPeriod(i?.timePeriod)}
            </span>
          </SmartTooltip>
        ),
        "Invoice Number": (
          <SmartTooltip
            description={i?.invoiceNum}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal
          >
            <span className="line-clamp-1">{i?.invoiceNum || ""}</span>
          </SmartTooltip>
        ),
        "Invoice Date": dayjs(i.invoiceDate).format("MM/DD/YYYY"),
        "Funding Status": (
          <Chip
            label={fundindInvoicesStatus[i?.fundingStatus]?.label}
            color={fundindInvoicesStatus[i?.fundingStatus]?.color}
            className={fundindInvoicesStatus[i?.fundingStatus]?.className}
          />
        ),
        "Invoice Amount": i?.amounts?.invoiced || 0,
      }));
      return {
        invoicesFunding: mappedInvoices,
        invoicesFundingTotal: invoicesFundingTotal,
      };
    } catch (error) {
      console.error("Error in fetchInvoices:", error);
    }
  };

  const handleDashboardCalls = async () => {
    const startTime = performance.now();
    try {
      if (!dates.cards.from || !dates.cards.to) return;
      setIsLoading(true);
      const [dashboardCards, invoices, agingReport, chargeBackReport, purchaseSummaryReport, reserveDetailReport] =
        await Promise.allSettled([
          fetchFundingCards(dates.cards.from, dates.cards.to),
          fetchFundingInvoices(
            dates.cards.from,
            dates.cards.to,
            invLimit,
            invPage,
          ),
          fetchAgingReport(),
          fetchChargeBackReport(),
          fetchPurchaseSummaryReport(),
          fetchReserveDetailReport()
        ]);
      const agingData =
        agingReport?.value?.data?.report?.map((a) => ({
          client: a?.client,
          invoices:
            a?.invoices?.map((inv) => ({
              inv: inv?.invoiceNum,
              date: dateFormator(inv?.invoiceDate),
              paidDate: "",
              batch: inv?.batchNum,
              amount: inv?.amounts?.invoiced?.amt,
              bal: inv?.amounts?.balance,
              age: inv?.age,
              d1_30:
                inv?.amounts?.invoiced?.duration === 1
                  ? inv?.amounts?.invoiced?.amt
                  : 0,
              d31_60:
                inv?.amounts?.invoiced?.duration === 2
                  ? inv?.amounts?.invoiced?.amt
                  : 0,
              d61_90:
                inv?.amounts?.invoiced?.duration === 3
                  ? inv?.amounts?.invoiced?.amt
                  : 0,
              d91_120:
                inv?.amounts?.invoiced?.duration === 4
                  ? inv?.amounts?.invoiced?.amt
                  : 0,
              d121:
                inv?.amounts?.invoiced?.duration === 5
                  ? inv?.amounts?.invoiced?.amt
                  : 0,
            })) || [],
          total: {
            bal: a?.summary?.total || 0,
            d1_30: a?.summary?.days1to30 || 0,
            d31_60: a?.summary?.days31to60 || 0,
            d61_90: a?.summary?.days61to90 || 0,
            d91_120: a?.summary?.days91to120 || 0,
            d121: a?.summary?.days121Plus || 0,
          },
        })) || [];
      const chargeBackData =
        chargeBackReport?.value?.data?.report?.map((row) => ({
          customer: row?.client || "",
          rows:
            row?.invoices?.map((invoice) => ({
              posted: dateFormator(invoice?.invoiceDate) || "",
              invoiceNo: invoice?.invoiceNum || "",
              purchased: dateFormator(invoice?.purchasedOn) || "",
              buyNo: invoice?.buyNo || "",
              invoiceAmount: invoice?.amounts?.invoiced?.amt || 0.0,
              amountPaid: invoice?.amounts?.paid || 0.0,
              paidOn: invoice?.paidOn,
              colNo: invoice?.col || "",
              giveBack: invoice?.giveBack || 0,
              chargeBack: invoice?.chargeBack || 0.0,
              writeOffer: invoice?.writeOff || null,
              osDescription: invoice?.osDesc || "",
            })) || [],
          total: {
            amountPaid: row?.invoices?.reduce(
              (acc, invoice) => acc + (invoice?.amounts?.paid || 0),
              0,
            ),
            giveBack: row?.invoices?.reduce(
              (acc, invoice) => acc + (invoice?.giveBack || 0),
              0,
            ),
            chargeBack: row?.invoices?.reduce(
              (acc, invoice) => acc + (invoice?.chargeBack || 0),
              0,
            ),
          },
        })) || [];
      const purchaseSummaryData =
        purchaseSummaryReport?.value?.data?.report?.map((row) => ({
          customer: row?.client || "",
          debitCount: row?.debitCount || 0.0,
          debitAmount: row?.debit || 0.0,
          creditCount: "",
          creditAmount: "",
          amountPaid: "",
          totalAmount: row?.total || 0.0,
        })) || [];
      const reserveType = {
        1 : "Cash",
        2 : "Buy",
        3 : "Adj",
        4 : "Rsv",
      }
      const reserveDetailData = reserveDetailReport?.value?.data?.report?.map((row) => ({
          date: dateFormator(row?.reserveDate) || "",
          type: reserveType[row?.type] || "",
          description: row?.description || "Payment Reports",
          reference: row?.reference || "",
          rows: row?.invoices?.map((invoice) => ({
            invoiceNo: invoice?.invoiceNum || "",
            reference: "",
            customer: invoice?.client || "",
            buyDate: dateFormator(invoice?.buyDate) || "",
            daysOpen: invoice?.daysOpen || "-",
            activityType: invoice?.activityType || "-",
            appliedAR: invoice?.amounts?.ar || 0,
            appliedAdvance: invoice?.amounts?.advance || 0,
            appliedFee: invoice?.amounts?.fee || 0,
            feePct: invoice?.feePercent || 0,
            reserveAmount: invoice?.amounts?.reserve || 0,
          })) || [],
          total: {
            appliedAR: row?.invoices?.map?.((invoice) => invoice?.amounts?.ar || 0)?.reduce((a, b) => a + b, 0),
            appliedAdvance: row?.invoices?.map?.((invoice) => invoice?.amounts?.advance || 0)?.reduce((a, b) => a + b, 0),
            appliedFee: row?.invoices?.map?.((invoice) => invoice?.amounts?.fee || 0)?.reduce((a, b) => a + b, 0),
            reserveAmount: row?.invoices?.map?.((invoice) => invoice?.amounts?.reserve || 0)?.reduce((a, b) => a + b, 0),
          },
        })) || [];
      
      setData((prev) => ({
        ...prev,
        overviewCards: dashboardCards?.value,
        invoicesFunding:
          invoices?.value?.invoicesFunding || prev.invoicesFunding,
        agingReport: agingData,
        agingReportChart:
          agingData?.map((i) => ({
            "Client Name": (
              <SmartTooltip
                description={i?.client || ""}
                size="xs"
                color="black"
                noicon
                tooltipPosition="bottom"
                portal
              >
                <span className="line-clamp-1 min-w-[150px] max-w-[300px]">
                  {i?.client || ""}
                </span>
              </SmartTooltip>
            ),
            Balance: i.total.bal,
            "1-30 Days": i.total.d1_30,
            "31-60 Days": i.total.d31_60,
            "61-90 Days": i.total.d61_90,
            "91-120 Days": i.total.d91_120,
            "121+ Days": i.total.d121,
          })) || [],
        // agingReport: [],
        chargeBackReport: chargeBackData,
        chargeBackChart: chargeBackData?.map((client) => ({
          Customer: (
            <SmartTooltip
              description={client.customer}
              size="xs"
              color="black"
              noicon
              tooltipPosition="bottom"
              portal
            >
              <span className="line-clamp-1 min-w-[150px] max-w-[300px]">
                {client.customer || ""}
              </span>
            </SmartTooltip>
          ),
          "Amount Paid": client?.total?.amountPaid,
          "Give Back": client?.total?.giveBack,
          "Charge Back": client?.total?.chargeBack,
        })) || [],
        purchaseSummaryReport: {
          client: "",
          rows: purchaseSummaryData
        },
        purchaseSummaryChart: purchaseSummaryData?.map((client) => ({
          Customer: (
            <SmartTooltip
              description={client?.customer}
              size="xs"
              color="black"
              noicon
              tooltipPosition="bottom"
              portal
            >
              <span className="line-clamp-1 min-w-[150px] max-w-[300px]">
                {client.customer || ""}
              </span>
            </SmartTooltip>
          ),
          "Debit Amount": client?.debitAmount,
          "Credit Amount": client?.creditAmount,
          "Total Amount": client?.totalAmount,
        })) || [],
        reserveDetailReport: reserveDetailData,
        reserveDetailChart: reserveDetailData?.map(row => ({
          Description: (
            <SmartTooltip
              description={row?.reference || ""}
              size="xs"
              color="black"
              noicon
              tooltipPosition="bottom"
              portal
            >
              <span className="line-clamp-1 min-w-[150px] max-w-[300px]">
                {row.description || ""}
              </span>
            </SmartTooltip>
          ),
          "Applied To A/R": row.total.appliedAR,
          "Applied To Advance": row.total.appliedAdvance,
          "Applied To Fee": row.total.appliedFee,
          "Reserve Amount": row.total.reserveAmount,
        })) || [],
      }));
      setTotalCounts((prev) => ({
        ...prev,
        inv: invoices?.value?.invoicesFundingTotal,
      }));
      const endTime = performance.now();
      console.assert(
        import.meta.env.VITE_APP_ENV !== "local",
        `Time complexity: ${Number(endTime - startTime).toFixed(2)}ms`,
      );
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    handleDashboardCalls();
  }, [invPage, invLimit]);

  if (kpiModalOpen) {
    return (
      <InvoiceKPICard
        onClose={() => {
          setKpiModalOpen(false);
          setKpiModalType(null);
        }}
        cardsDuration={dates?.cards}
        dataType={kpiModalType}
      />
    );
  }

  return (
    <>
      <div className="px-1 py-2 flex flex-col gap-4">
        <>
          {import.meta.env.VITE_APP_ENV === "local" && (
            <div className="flex items-center justify-end col-span-2 w-max-[50px]">
              <Button
                onClick={() => {
                  console.groupCollapsed("--- New Log Session ---");
                  console.groupEnd();
                  handleDashboardCalls();
                }}
                variant="outlined"
                icon="autorenew"
                title={"Refresh"}
              />
            </div>
          )}
        </>
        <div className="bg-[#FAFCFF] col-span-2 flex flex-col px-3 py-4 border border-gray-200 rounded-2xl shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h5>Funding Overview</h5>
            <div className="w-full max-w-[200px]">
              <FormFieldSet
                noTitle
                placeholder="Select date range"
                type="monthpicker"
                // value={{
                //   from: dates?.cards?.from || null,
                //   to: dates?.cards?.to || null,
                // }}
                value={dates?.cards?.from || null}
                onChange={(value) => handleDateRangeCards("cards", value)}
                showDropdown={false}
              />
            </div>
          </div>

          <InfoWrapper className={"min-w-[250px]"}>
            {data?.overviewCards?.map((item, _idx) => (
              <InfoCardNewAdv
                key={_idx}
                title={item?.title}
                value={item?.value}
                prefix={item?.prefix || ""}
                suffix={item?.suffix || ""}
                layout="option9"
                onClick={() => {
                  setKpiModalOpen(true);
                  setKpiModalType(item?.title);
                }}
              />
            ))}
          </InfoWrapper>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#FAFCFF] h-[340px] col-span-2 px-3 py-4 border border-gray-200 rounded-2xl shadow-lg">
            <ChartNew
              type="tablechart"
              title=""
              rawData={data?.invoicesFunding || []}
              chartLabel={"Emp Name"}
              chartValue={"Invoice Amount"}
              onExportClick={handleExport}
              barColors={[
                { color: "primary" },
                { color: "info" },
                { color: "leave" },
                { color: "draft", depth: 500 },
                { color: "success" },
              ]}
              chartToolbarRight={{
                filter: true,
                expand: true,
                screenShare: true,
                chartToggle: true,
                saveButton: selectedIds.length > 0,
                onSaveClick: invoiceStatusChange,
                date: false,
                selectDropdown: true,
                selectDropdownOptions: DropdownOptions,
                selectDropdownValue: selectedOption,
                onSelectDropdownChange: handleFundingSelect,
                showInBothViews: true,
              }}
              // onDateChange={(value) => handleDateRangeCards("invoices", value)}
              tableChartProps={{
                // if total prop is defined uncomment this line
                totalCount: totalCounts.inv,
                // hideToolBar: false,
                showCheckbox: true,
                hideToolBarInNormalView: false,
                hideToolBarInExpandedView: false,
                onSelectionChange: handleSelection,
                defaultSelectedRows: selectedIds,
                idKey: "id",
                hideIdColumn: true,
                page: invPage,
                limit: invLimit,
                serverPagination: true,
                onPageChange: invSetPage,
                onLimitChange: invHandleLimitChange,
              }}
            />
          </div>

          <div className="bg-[#FAFCFF] h-[340px] px-3 py-4 border border-gray-200 rounded-2xl shadow-lg col-span-2 lg:col-span-1">
            <ChartNew
              type="tablechart"
              title="Reserved Detailed Report"
              barColors={[
                { color: "primary" },
                { color: "info" },
                { color: "leave" },
                { color: "draft", depth: 500 },
                { color: "success" },
              ]}
              onCollapse={() => setFormData(initialFormData)}
              chartToolbarRight={{
                chartToggle: true,
                expand: true,
                upload: true,
                showInBothViews: false,
              }}
              onUploadClick={handleReservedUpload}
              // rawData={reservedData}
              rawData={data?.reserveDetailChart || []}
              chartLabel={"Description"}
              chartValue={"Reserve Amount"}
              hideExpandedChart={true}
              expandedContentPosition="top"
              expandedContent={
                <>
                  <ReservedFilterBar
                    formData={formData}
                    handleChange={handleChange}
                    activityOpen={activityOpen}
                    setActivityOpen={setActivityOpen}
                    setReservedUpload={setReservedUpload}
                  />
                  <ReservedModalTable
                    rows={data?.reserveDetailReport || []}
                    columns={reservedColumns}
                  />
                </>
              }
              areaChartProps={{
                steps: 5,
                depth: 400,
                renderToolTip: (data) => {
                  return (
                    <div className="flex flex-col gap-3 text-sm">
                      <span className="text-primary">{data?.label}</span>

                      <div className="bg-primary-50 text-gray-600 text-xs font-medium w-fit flex justify-between p-2 rounded shadow-md">
                        Reserve Amount :
                        <span className="text-primary ml-1">{data?.value}</span>
                      </div>
                    </div>
                  );
                },
              }}
            />
          </div>

          <div className="bg-[#FAFCFF] h-[340px] px-3 py-4 border border-gray-200 rounded-2xl shadow-lg col-span-2 lg:col-span-1">
            <ChartNew
              type="tablechart"
              title="Aging Report"
              chartToolbarRight={{
                chartToggle: true,
                expand: true,
                upload: true,
              }}
              barColors={[
                { color: "primary" },
                { color: "info" },
                { color: "leave" },
                { color: "draft", depth: 500 },
                { color: "success" },
              ]}
              // rawData={agingData}
              rawData={data?.agingReportChart || []}
              onUploadClick={handleAgingUpload}
              chartLabel={"Client Name"}
              chartValue={"Balance"}
              onCollapse={() => setFormData(initialFormData)}
              expandedContentPosition="top"
              hideExpandedChart={true}
              expandedContent={
                <>
                  <AgingFilterBar
                    formData={formData}
                    handleChange={handleChange}
                    activityOpen={activityOpen}
                    setActivityOpen={setActivityOpen}
                    setAgingUpload={setAgingUpload}
                  />
                  <UploadModalTable
                    rows={data?.agingReport || []}
                    columns={agingColumns}
                  />
                </>
              }
              areaChartProps={{
                steps: 5,
                depth: 400,
                renderToolTip: (data) => {
                  return (
                    <div className="flex flex-col gap-3 text-sm">
                      <span className="text-primary">{data?.label}</span>

                      <div className="bg-primary-50 text-gray-600 text-xs font-medium w-fit flex justify-between p-2 rounded shadow-md">
                        Balance :
                        <span className="text-primary ml-1">{data?.value}</span>
                      </div>
                    </div>
                  );
                },
              }}
            />
          </div>

          <div className="bg-[#FAFCFF] flex flex-col h-[400px] px-3 py-4 border border-gray-200 rounded-2xl shadow-lg col-span-2 lg:col-span-1">
            <ChartNew
              type="tablechart"
              title="Charge Back"
              chartToolbarRight={{
                chartToggle: true,
                expand: true,
                upload: true,
              }}
              barColors={[
                { color: "primary" },
                { color: "info" },
                { color: "leave" },
                { color: "draft", depth: 500 },
                { color: "success" },
              ]}
              onCollapse={() => setFormData(initialFormData)}
              rawData={data?.chargeBackChart}
              onUploadClick={handleChargeBackUpload}
              chartLabel={"Customer"}
              chartValue={"Amount Paid"}
              tableChartProps={{
                tableTotalKey: ["Charge Back", "Amount Paid", "Give Back"],
              }}
              hideExpandedChart={true}
              expandedContent={
                <>
                  <ChargeBackFilterBar
                    formData={formData}
                    handleChange={handleChange}
                    activityOpen={activityOpen}
                    setActivityOpen={setActivityOpen}
                    setChargeBackUpload={setChargeBackUpload}
                  />
                  <ChargeBackReportTable
                    rows={data?.chargeBackReport}
                    columns={chargeBackColumns}
                  />
                </>
              }
              areaChartProps={{
                steps: 5,
                depth: 400,
                renderToolTip: (data) => {
                  return (
                    <div className="flex flex-col gap-3 text-sm">
                      <span className="text-primary">{data?.label}</span>

                      <div className="bg-primary-50 text-gray-600 text-xs font-medium w-fit flex justify-between p-2 rounded shadow-md">
                        Amount Paid :
                        <span className="text-primary ml-1">{data?.value}</span>
                      </div>
                    </div>
                  );
                },
              }}
            />
          </div>

          <div className="bg-[#FAFCFF] flex flex-col h-[400px] px-3 py-4 border border-gray-200 rounded-2xl shadow-lg col-span-2 lg:col-span-1">
            <ChartNew
              title="Purchase Summary Report"
              chartToolbarRight={{
                chartToggle: true,
                expand: true,
                upload: true,
              }}
              barColors={[
                { color: "primary" },
                { color: "info" },
                { color: "leave" },
                { color: "draft", depth: 500 },
                { color: "success" },
              ]}
              onCollapse={() => setFormData(initialFormData)}
              rawData={data?.purchaseSummaryChart}
              onUploadClick={handlePurchaseSummaryUpload}
              type="tablechart"
              chartLabel={"Customer"}
              chartValue={"Total Amount"}
              tableChartProps={{
                tableTotalKey: [
                  "Total Amount",
                  "Debit Amount",
                  "Credit Amount",
                ],
              }}
              hideExpandedChart={true}
              expandedContent={
                <>
                  <PurchaseSummaryFilterBar
                    formData={formData}
                    handleChange={handleChange}
                    activityOpen={activityOpen}
                    setActivityOpen={setActivityOpen}
                    setFormData={setFormData}
                    setPurchaseSummaryUpload={setPurchaseSummaryUpload}
                  />
                  <PurchaseSummaryReportTable
                    rows={data?.purchaseSummaryReport}
                    columns={purchaseSummaryColumns}
                  />
                </>
              }
              areaChartProps={{
                steps: 5,
                depth: 400,
                renderToolTip: (data) => {
                  return (
                    <div className="flex flex-col gap-3 text-sm">
                      <span className="text-primary">{data?.label}</span>

                      <div className="bg-primary-50 text-gray-600 text-xs font-medium w-fit flex justify-between p-2 rounded shadow-md">
                        Total Amount :
                        <span className="text-primary ml-1">{data?.value}</span>
                      </div>
                    </div>
                  );
                },
              }}
            />

            {/* <div className="flex justify-end items-center text-white gap-1 mt-1">
          <div className="bg-primary w-[200px] py-3 px-4 rounded-l-lg">
            Total
          </div>
          <div className="bg-primary w-[200px] py-3 px-4 rounded-r-lg">
            30,400
          </div>
        </div> */}
          </div>
        </div>
      </div>
      <Modal
        isOpen={chargeBackUpload}
        onClose={() => {
          setChargeBackUpload(false);
          setFormData((prev) => ({
            ...initialFormData,
            fromDate: prev.fromDate,
            toDate: prev.toDate,
            reportPeriod: prev.reportPeriod,
            search: prev.search,
          }));
        }}
        title="Upload"
        width="max-w-3xl"
        titleColor={"primary"}
        zIndex={55}
      >
        <ChargeBackUploadModal
          onClose={() => setChargeBackUpload(false)}
          isOpen={chargeBackUpload}
          formData={formData}
          handleChange={handleChange}
          setFormData={setFormData}
          initialFormData={initialFormData}
          rows={chargeBackRows}
          columns={chargeBackColumns}
          loginUserUiid={loginUserUiid}
          handleDashboardCalls={handleDashboardCalls}
        />
      </Modal>
      <Modal
        isOpen={reservedUpload}
        onClose={() => {
          setReservedUpload(false);
          setFormData((prev) => ({
            ...initialFormData,
            fromDate: prev.fromDate,
            toDate: prev.toDate,
            reportPeriod: prev.reportPeriod,
            search: prev.search,
          }));
        }}
        title="Upload"
        width="max-w-3xl"
        titleColor={"primary"}
        zIndex={55}
      >
        <ReservedUploadModal
          onClose={() => setReservedUpload(false)}
          isOpen={reservedUpload}
          formData={formData}
          handleChange={handleChange}
          setFormData={setFormData}
          initialFormData={initialFormData}
          rows={reservedRows}
          columns={reservedColumns}
          loginUserUiid={loginUserUiid}
          handleDashboardCalls={handleDashboardCalls}
        />
      </Modal>
      <Modal
        isOpen={agingUpload}
        onClose={() => {
          setAgingUpload(false);
          setFormData((prev) => ({
            ...initialFormData,
            fromDate: prev.fromDate,
            toDate: prev.toDate,
            reportPeriod: prev.reportPeriod,
            search: prev.search,
          }));
        }}
        title="Upload"
        width="max-w-3xl"
        titleColor={"primary"}
        zIndex={55}
      >
        <UploadModal
          onClose={() => setAgingUpload(false)}
          isOpen={agingUpload}
          formData={formData}
          handleChange={handleChange}
          setFormData={setFormData}
          initialFormData={initialFormData}
          rows={agingRows}
          columns={agingColumns}
          loginUserUiid={loginUserUiid}
          handleDashboardCalls={handleDashboardCalls}
        />
      </Modal>
      <Modal
        isOpen={purchaseSummaryUpload}
        onClose={() => {
          setPurchaseSummaryUpload(false);
          setFormData((prev) => ({
            ...initialFormData,
            fromDate: prev.fromDate,
            toDate: prev.toDate,
            reportPeriod: prev.reportPeriod,
            search: prev.search,
          }));
        }}
        title="Upload"
        width="max-w-3xl"
        titleColor={"primary"}
        zIndex={55}
      >
        <PurchaseSummaryUploadModal
          onClose={() => setPurchaseSummaryUpload(false)}
          isOpen={purchaseSummaryUpload}
          formData={formData}
          handleChange={handleChange}
          setFormData={setFormData}
          initialFormData={initialFormData}
          rows={purchaseSummaryRows}
          columns={purchaseSummaryColumns}
          loginUserUiid={loginUserUiid}
          handleDashboardCalls={handleDashboardCalls}
        />
      </Modal>

      <Backdrop isOpen={isLoading}>
        <LogoLoader />
      </Backdrop>
    </>
  );
};

export default FundingDashboard;
